import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button, Form, Row, Col, Alert, ListGroup, Badge, Table } from 'react-bootstrap';
import { notificar } from '../../components/SistemaToast/notificar';
import ElegantModal from '../../components/ElegantModal/ElegantModal';
import { urlApiProd } from '../utiles';
import { sumPesoGrossByMergeGroups, sumPesoNetByMergeGroups } from './invoiceWeightUtils';
import {
  buildInvoicePtcMerchandise,
  formatPcsPaqInvoiceCell,
  gradoConsolidadoFromPackingRow,
  mensajePtcPiPricingError,
  packingListTotals,
  productMeasureGradeKey,
  sortPtcMerchandiseLines,
} from './invoicePtcMerchandise';
import { buildInvoicePtbMerchandise } from './invoicePtbMerchandise';
import TextosMercaderiaPtcFields from '../../components/TextosMercaderiaPtcFields/TextosMercaderiaPtcFields';
import DateInput from '../../components/DateInput/DateInput';
import {
  pairFromInvoiceDatos,
  textosMercaderiaPtcFromCliente,
  textosMercaderiaPtcPayloadFromPair,
  truncateTextoMercaderiaPtc,
} from './invoicePtcTextosMercaderia';
import { packingListsSinInvoice } from './packingListSinInvoiceUtils';
import InvoicePtcPreviewModal from './InvoicePtcPreviewModal';
import {
  contractNoFromProformas,
  createInvoicePtcPdfBlobUrl,
  generateInvoicePtcPdf,
  invoicePtcParamsFromFormAndDetail,
  invoicePtcParamsFromFormWithPackingList,
  lineasPdfTienenPrecios,
  proformaFromLista,
  purchaseOrderFromProformas,
} from './generateInvoicePtcPDF';
import { proformaContractLabel } from './invoiceProformaUtils';
import {
  createInvoicePtbPdfBlobUrl,
  generateInvoicePtbPdf,
  invoicePtbParamsFromFormWithPackingList,
  mensajePtbSinPreciosLogistico,
} from './generateInvoicePtbPDF';
import {
  invoiceNumberCorrelativo,
  numeroParaVistaPrevia,
} from './invoiceNumberUtils';
import { totalInvoiceLabel } from './invoiceCurrencyLabel';
function precioUnitarioAnteriorAgrupado(lineas) {
  let m3ConPrecio = 0;
  let valorTotal = 0;
  const unitarios = [];
  for (const linea of lineas || []) {
    const cantidad = Number(linea.quantityM3);
    const unitario =
      linea.unitPriceUsd == null || linea.unitPriceUsd === ''
        ? null
        : Number(linea.unitPriceUsd);
    const total =
      linea.totalValueUsd == null || linea.totalValueUsd === ''
        ? null
        : Number(linea.totalValueUsd);
    if (unitario != null && Number.isFinite(unitario)) unitarios.push(unitario);
    if (!(Number.isFinite(cantidad) && cantidad > 0)) continue;
    if (Number.isFinite(total)) {
      m3ConPrecio += cantidad;
      valorTotal += total;
    } else if (unitario != null && Number.isFinite(unitario)) {
      m3ConPrecio += cantidad;
      valorTotal += cantidad * unitario;
    }
  }
  if (m3ConPrecio > 0) return Math.round((valorTotal / m3ConPrecio) * 100) / 100;
  return unitarios.length ? unitarios[unitarios.length - 1] : null;
}

function textoRequerido(value) {
  return value != null && String(value).trim() !== '';
}

function numeroRequerido(value) {
  return value !== '' && value != null && Number.isFinite(Number(value));
}

function mercanciaLineasSinPrecioUnitario(lineas) {
  return (lineas || []).some((ln) => {
    const m3 = Number(ln?.quantityM3);
    if (!Number.isFinite(m3) || m3 <= 0) return false;
    const u = ln?.unitPriceUsd;
    return u == null || u === '' || Number.isNaN(Number(u));
  });
}

function foldBusquedaTexto(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function proformaEtiqueta(pi, clientesLista = []) {
  return proformaContractLabel(pi, clientesLista);
}

function proformaBusquedaHaystack(pi, clientesLista = []) {
  if (!pi) return '';
  const lbl = proformaEtiqueta(pi, clientesLista);
  const idn = pi.id_proforma != null ? String(pi.id_proforma) : '';
  return `${lbl} ${idn}`.trim();
}

function ultimaProformaId(ids) {
  if (!ids?.length) return '';
  return String(ids[ids.length - 1]);
}

function appendProformaIdOrdenado(prevIds, id) {
  const s = String(id);
  const next = (prevIds || []).filter((x) => String(x) !== s);
  next.push(s);
  return next;
}

function getPiLabel(idProforma, proformasLista, clientesLista = []) {
  if (!idProforma) return '';
  const pi = proformasLista.find((p) => String(p.id_proforma) === String(idProforma));
  return proformaEtiqueta(pi, clientesLista);
}

function puertoNombreCorto(etiqueta) {
  const t = String(etiqueta || '').trim();
  const idx = t.indexOf(' (');
  return idx >= 0 ? t.slice(0, idx).trim() : t;
}

function buildClienteDireccionTexto(cli, snapshot) {
  const stripTrailingCountry = (text, country) => {
    if (!text || !country) return String(text || '').trim();
    const c = String(country).trim().toLowerCase();
    if (!c) return String(text).trim();
    const lines = String(text)
      .split(/\r?\n/)
      .map((ln) => ln.trim())
      .filter(Boolean)
      .filter((ln) => ln.toLowerCase() !== c);
    return lines.join('\n');
  };
  if (snapshot && String(snapshot).trim()) {
    return stripTrailingCountry(snapshot, cli?.pais);
  }
  if (!cli) return '';
  const parts = [];
  if (cli.direccion) parts.push(cli.direccion);
  if (cli.ciudad) parts.push(cli.ciudad);
  if (cli.codigo_postal) parts.push(`PIN CODE: ${String(cli.codigo_postal).trim()}`);
  // País no va en Address: se muestra en City/Country (paisDestino).
  return parts.join('\n');
}

function incotermDesdePi(proforma, incoterms, puertos) {
  if (!proforma) return '';
  const inco = incoterms.find((i) => String(i.id_incoterm) === String(proforma.incoterm));
  const pto = puertos.find((p) => String(p.id_puerto) === String(proforma.puerto));
  const nombreInco = inco?.nombre || '';
  const ptoTxt = pto ? `${pto.nombre}, ${pto.pais || ''}`.trim() : '';
  return [nombreInco, ptoTxt].filter(Boolean).join(' ').trim();
}

function paymentDesdePi(proforma, paymentTerms) {
  if (!proforma?.payment_terms) return '';
  const pt = paymentTerms.find(
    (p) => String(p.id_paymentterm) === String(proforma.payment_terms)
  );
  return pt?.nombre || '';
}

const EMPRESA_PTC = 0;
const EMPRESA_PTB = 1;

/**
 * Formulario Invoice (mismo shell que Crear packing list).
 * PTC: emite PDF PTC + PDF PTB. PTB: solo PDF PTB con reglas de campos PTC.
 */
/** Menú autocomplete fuera del modal (evita recorte por overflow del panel). */
function BookingAutocompletePortal({ open, anchorRef, children }) {
  const [menuStyle, setMenuStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setMenuStyle(null);
      return undefined;
    }
    const update = () => {
      const r = anchorRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: r.bottom + 2,
        left: r.left,
        width: r.width,
        zIndex: 10060,
        maxHeight: 220,
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef]);

  if (!open || !menuStyle) return null;
  return createPortal(
    <ListGroup className="shadow border rounded overflow-auto bg-white" style={menuStyle}>
      {children}
    </ListGroup>,
    document.body
  );
}

const AgregarInvoice = ({
  show,
  cerrar,
  invoiceEditId = null,
  packingListDisponibles = [],
  onGuardarExito,
}) => {
  const isEditMode = invoiceEditId != null && invoiceEditId !== '';
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [puertos, setPuertos] = useState([]);
  const [idsProformaSeleccion, setIdsProformaSeleccion] = useState([]);
  const [proformaBusqueda, setProformaBusqueda] = useState('');
  const [proformaMenuOpen, setProformaMenuOpen] = useState(false);
  const proformaInputRef = useRef(null);
  const proformaElegidaRecienteRef = useRef(false);

  const [empresaEmisor, setEmpresaEmisor] = useState(EMPRESA_PTC);
  const [idPackingList, setIdPackingList] = useState(null);
  const [bookingBusqueda, setBookingBusqueda] = useState('');
  const [bookingMenuOpen, setBookingMenuOpen] = useState(false);
  const [packingDisponiblesLocal, setPackingDisponiblesLocal] = useState([]);
  const bookingAnchorRef = useRef(null);

  const [fechaEmbarque, setFechaEmbarque] = useState('');
  const [valorUsd, setValorUsd] = useState('');
  const [valorUsdPtb, setValorUsdPtb] = useState('');
  const [ajustesPtc, setAjustesPtc] = useState([]);
  const [valorFobPtc, setValorFobPtc] = useState('');
  const [valorFletePtc, setValorFletePtc] = useState('');
  const [valorSeguroPtc, setValorSeguroPtc] = useState('');
  const [textoMercaderiaPtcArriba, setTextoMercaderiaPtcArriba] = useState('');
  const [textoMercaderiaPtcAbajo, setTextoMercaderiaPtcAbajo] = useState('');
  const [lineasMercanciaEditables, setLineasMercanciaEditables] = useState([]);
  const [lineasMercanciaPtbEditables, setLineasMercanciaPtbEditables] = useState([]);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [especies, setEspecies] = useState([]);
  const [calidades, setCalidades] = useState([]);
  const [totalContenedores, setTotalContenedores] = useState('');
  const [totalM3, setTotalM3] = useState('');
  const [hsCodeSnapshot, setHsCodeSnapshot] = useState('');
  const [productDescriptionSnapshot, setProductDescriptionSnapshot] = useState('');
  const [idCliente, setIdCliente] = useState('');
  const [clienteBusqueda, setClienteBusqueda] = useState('');
  const [clienteMenuOpen, setClienteMenuOpen] = useState(false);
  const [clienteDireccion, setClienteDireccion] = useState('');
  const [taxId, setTaxId] = useState('');
  const [pinCode, setPinCode] = useState('');

  const [paisOrigen, setPaisOrigen] = useState('');
  const [paisDestino, setPaisDestino] = useState('');
  const [nombreNave, setNombreNave] = useState('');
  const [puertoOrigen, setPuertoOrigen] = useState('');
  const [puertoDestino, setPuertoDestino] = useState('');
  const [lugarEntrega, setLugarEntrega] = useState('');
  const [nombreBl, setNombreBl] = useState('');
  const [contractNo, setContractNo] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  const [netWeight, setNetWeight] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [salesTerms, setSalesTerms] = useState('AFIRME');
  const [incotermsTexto, setIncotermsTexto] = useState('');
  const [paymentTermsPtc, setPaymentTermsPtc] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unidadVolumenPtc, setUnidadVolumenPtc] = useState('M3');
  const [invoiceCurrency, setInvoiceCurrency] = useState('USD');

  const [contractNoPtb, setContractNoPtb] = useState('');
  const [purchaseOrderPtb, setPurchaseOrderPtb] = useState('PTB-01');
  const [termsOfSalePtb, setTermsOfSalePtb] = useState('AFIRME');
  const [clausePtb, setClausePtb] = useState('');
  const [paymentTermsPtb, setPaymentTermsPtb] = useState('WIRE TRANSFER ADVANCE 100%');

  const [numeroIdentificadorPtc, setNumeroIdentificadorPtc] = useState('');
  const [numeroIdentificadorPtb, setNumeroIdentificadorPtb] = useState('');
  const [estadoInvoice, setEstadoInvoice] = useState('prefactura');

  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrls, setPreviewUrls] = useState({ PTC: null, PTB: null });
  const [previewVariant, setPreviewVariant] = useState('PTC');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewWarning, setPreviewWarning] = useState('');
  const previewParamsRef = useRef(null);
  /** Evita recargar catálogo + invoice en bucle al hidratar el formulario. */
  const invoiceModalLoadKeyRef = useRef(null);
  const hydratingEditRef = useRef(false);

  const bookingElegidoRecienteRef = useRef(false);
  const detalleLineasRef = useRef([]);
  const idProformaUltimaRef = useRef(null);
  const mercPayloadRef = useRef(null);
  const lineasMercanciaEditablesRef = useRef([]);
  const lineasMercanciaPtbEditablesRef = useRef([]);
  const valorUsdEditadoRef = useRef(false);
  const valorUsdPtbEditadoRef = useRef(false);
  const purchaseOrderEditadoRef = useRef(false);
  const purchaseOrderPtbEditadoRef = useRef(false);

  const textoEmpresaPtc = 'PALM TRADE CHILE SPA';
  const textoEmpresaPtb = 'PALM TRADE BRASIL LTDA';

  const revokePreviewUrls = useCallback((urls) => {
    if (!urls) return;
    Object.values(urls).forEach((u) => {
      if (u) URL.revokeObjectURL(u);
    });
  }, []);

  const packingDisponiblesLista = useMemo(() => {
    if (packingDisponiblesLocal.length) return packingDisponiblesLocal;
    return packingListDisponibles || [];
  }, [packingDisponiblesLocal, packingListDisponibles]);

  const bookingsOpciones = useMemo(() => {
    const qf = foldBusquedaTexto(bookingBusqueda);
    const base = packingDisponiblesLista.filter((r) => r.nro_booking);
    if (!qf) return base.slice(0, 80);
    return base
      .filter((r) => foldBusquedaTexto(r.nro_booking).includes(qf))
      .slice(0, 80);
  }, [packingDisponiblesLista, bookingBusqueda]);

  const proformasFiltradas = useMemo(() => {
    if (!idCliente) return proformas;
    return proformas.filter(
      (p) => p.cliente != null && String(p.cliente) === String(idCliente)
    );
  }, [proformas, idCliente]);

  const idProformaUltima = useMemo(
    () => ultimaProformaId(idsProformaSeleccion),
    [idsProformaSeleccion]
  );

  const proformasOrdenadas = useMemo(
    () =>
      [...proformasFiltradas].sort((a, b) =>
        proformaEtiqueta(a).localeCompare(proformaEtiqueta(b), 'es', { sensitivity: 'base' })
      ),
    [proformasFiltradas]
  );

  const proformasBusquedaLista = useMemo(() => {
    const yaSeleccion = new Set((idsProformaSeleccion || []).map((id) => String(id)));
    const candidatas = proformasOrdenadas.filter(
      (pi) => !yaSeleccion.has(String(pi.id_proforma))
    );
    const qf = foldBusquedaTexto(proformaBusqueda);
    if (!qf) return candidatas.slice(0, 80);
    return candidatas
      .filter((pi) => foldBusquedaTexto(proformaBusquedaHaystack(pi)).includes(qf))
      .slice(0, 80);
  }, [proformasOrdenadas, proformaBusqueda, idsProformaSeleccion]);

  const clientesFiltrados = useMemo(() => {
    const qf = foldBusquedaTexto(clienteBusqueda);
    const sorted = [...clientes].sort((a, b) =>
      (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
    );
    if (!qf) return sorted.slice(0, 80);
    return sorted
      .filter((c) => foldBusquedaTexto(c.nombre || '').includes(qf))
      .slice(0, 80);
  }, [clientes, clienteBusqueda]);

  const resetForm = useCallback(() => {
    setEmpresaEmisor(EMPRESA_PTC);
    setValorUsd('');
    setValorUsdPtb('');
    setAjustesPtc([]);
    setValorFobPtc('');
    setValorFletePtc('');
    setValorSeguroPtc('');
    setTextoMercaderiaPtcArriba('');
    setTextoMercaderiaPtcAbajo('');
    lineasMercanciaEditablesRef.current = [];
    setLineasMercanciaEditables([]);
    lineasMercanciaPtbEditablesRef.current = [];
    setLineasMercanciaPtbEditables([]);
    valorUsdEditadoRef.current = false;
    valorUsdPtbEditadoRef.current = false;
    purchaseOrderEditadoRef.current = false;
    purchaseOrderPtbEditadoRef.current = false;
    setTotalContenedores('');
    setTotalM3('');
    setHsCodeSnapshot('');
    setProductDescriptionSnapshot('');
    setIdPackingList(null);
    setBookingBusqueda('');
    setBookingMenuOpen(false);
    setFechaEmbarque('');
    setIdCliente('');
    setClienteBusqueda('');
    setClienteMenuOpen(false);
    setClienteDireccion('');
    setTaxId('');
    setPinCode('');
    setPaisOrigen('');
    setPaisDestino('');
    setNombreNave('');
    setPuertoOrigen('');
    setPuertoDestino('');
    setLugarEntrega('');
    setNombreBl('');
    setContractNo('');
    setIdsProformaSeleccion([]);
    setProformaBusqueda('');
    setProformaMenuOpen(false);
    setPurchaseOrder('');
    setNetWeight('');
    setGrossWeight('');
    setSalesTerms('AFIRME');
    setIncotermsTexto('');
    setPaymentTermsPtc('');
    setQuantity('');
    setUnidadVolumenPtc('M3');
    setInvoiceCurrency('USD');
    detalleLineasRef.current = [];
    idProformaUltimaRef.current = null;
    mercPayloadRef.current = null;
    previewParamsRef.current = null;
    setPreviewUrls((prev) => {
      revokePreviewUrls(prev);
      return { PTC: null, PTB: null };
    });
    setPreviewVariant('PTC');
    setShowPreview(false);
    setPreviewError('');
    setContractNoPtb('');
    setPurchaseOrderPtb('PTB-01');
    setTermsOfSalePtb('AFIRME');
    setClausePtb('');
    setPaymentTermsPtb('WIRE TRANSFER ADVANCE 100%');
    setNumeroIdentificadorPtc('');
    setNumeroIdentificadorPtb('');
    setEstadoInvoice('prefactura');
    setErrors({});
    setSaveError('');
  }, [revokePreviewUrls]);

  const aplicarMercanciaDesdePl = useCallback(
    (pl, pi, numeroInv) => {
      const lineas = pl?.lineas || [];
      detalleLineasRef.current = lineas;
      idProformaUltimaRef.current = pi || null;
      const totals = packingListTotals(lineas);
      const merc = buildInvoicePtcMerchandise(lineas, pi, {
        currency: invoiceCurrency,
        fechaInvoice: fechaEmbarque || pl?.fecha_embarque,
        fechaEmbarque: pl?.fecha_embarque || fechaEmbarque,
        numeroInvoice: numeroInv ?? numeroIdentificadorPtc,
        productDescriptionSnapshot:
          pl?.product_description_snapshot || productDescriptionSnapshot,
        calidades,
        clientes,
        productos,
        categorias,
        proformasLista: proformas,
        idProformas: idsProformaSeleccion,
      });
      const anteriores = lineasMercanciaEditablesRef.current || [];
      const lineasEditables = merc.lineasMercancia.map((linea) => {
        const key = productMeasureGradeKey(linea);
        const anterioresCompatibles = anteriores.filter(
          (item) => productMeasureGradeKey(item) === key
        );
        const anterior = anterioresCompatibles.length
          ? { unitPriceUsd: precioUnitarioAnteriorAgrupado(anterioresCompatibles) }
          : null;
        const precioAnteriorValido =
          anterior?.unitPriceUsd != null && Number.isFinite(Number(anterior.unitPriceUsd));
        const precioAnteriorEditado =
          precioAnteriorValido &&
          (linea.unitPriceUsd == null ||
            Number(anterior.unitPriceUsd) !== Number(linea.unitPriceUsd));
        // Conserva precio editado por grado/producto; no exige igualdad con el de la PI.
        const unitPriceUsd =
          precioAnteriorValido
            ? Number(anterior.unitPriceUsd)
            : linea.unitPriceUsd;
        const totalValueUsd = precioAnteriorEditado
          ? unitPriceUsd != null && Number(linea.quantityM3) > 0
            ? Math.round(Number(linea.quantityM3) * Number(unitPriceUsd) * 100) / 100
            : null
          : linea.totalValueUsd;
        return { ...linea, unitPriceUsd, totalValueUsd };
      });
      const lineasOrdenadas = sortPtcMerchandiseLines(lineasEditables);
      const totalEditables = Math.round(
        lineasOrdenadas.reduce((sum, ln) => sum + (Number(ln.totalValueUsd) || 0), 0) * 100
      ) / 100;
      lineasMercanciaEditablesRef.current = lineasOrdenadas;
      setLineasMercanciaEditables(lineasOrdenadas);
      mercPayloadRef.current = {
        lineasMercancia: lineasEditables,
        totalUsd: totalEditables > 0 ? totalEditables : merc.totalUsd,
        totalBundles: totals.totalBundles,
        totalM3: totals.totalM3,
      };
      // Valor total = suma de todas las líneas (CD + RW + …), no solo un grado.
      if (!valorUsdEditadoRef.current && totalEditables > 0) {
        setValorUsd(String(totalEditables));
      }
      const m3Txt = totals.totalM3 > 0 ? String(totals.totalM3) : '';
      if (m3Txt) setQuantity((prev) => (String(prev).trim() ? prev : m3Txt));
    },
    [
      invoiceCurrency,
      fechaEmbarque,
      numeroIdentificadorPtc,
      productDescriptionSnapshot,
      calidades,
      clientes,
      productos,
      categorias,
      proformas,
      idsProformaSeleccion,
    ]
  );

  const aplicarMercanciaPtbDesdePl = useCallback(
    (pl, pi, numeroInv) => {
      const lineas = pl?.lineas || [];
      const merc = buildInvoicePtbMerchandise(lineas, null, pi, {
        currency: invoiceCurrency,
        fechaInvoice: fechaEmbarque || pl?.fecha_embarque,
        fechaEmbarque: pl?.fecha_embarque || fechaEmbarque,
        numeroInvoice: numeroInv ?? numeroIdentificadorPtb,
        productDescriptionSnapshot:
          pl?.product_description_snapshot || productDescriptionSnapshot,
        calidades,
        productos,
        categorias,
      });
      const anteriores = lineasMercanciaPtbEditablesRef.current || [];
      const lineasEditables = merc.lineasMercancia.map((linea) => {
        const anterioresCompatibles = anteriores.filter(
          (item) =>
            String(item.productName || '').trim().toUpperCase() ===
              String(linea.productName || '').trim().toUpperCase() &&
            gradoConsolidadoFromPackingRow(item) === gradoConsolidadoFromPackingRow(linea) &&
            String(item.espessura || '') === String(linea.espessura || '') &&
            String(item.largura || '').toUpperCase() === String(linea.largura || '').toUpperCase() &&
            String(item.comprimento || '') === String(linea.comprimento || '')
        );
        const anterior = anterioresCompatibles.length
          ? { unitPriceUsd: precioUnitarioAnteriorAgrupado(anterioresCompatibles) }
          : null;
        const unitPriceUsd =
          anterior?.unitPriceUsd != null && Number.isFinite(Number(anterior.unitPriceUsd))
            ? Number(anterior.unitPriceUsd)
            : linea.unitPriceUsd;
        const userEditedUnit =
          anterior?.unitPriceUsd != null &&
          Number.isFinite(Number(anterior.unitPriceUsd)) &&
          linea.unitPriceUsd != null &&
          Number(anterior.unitPriceUsd) !== Number(linea.unitPriceUsd);
        const totalValueUsd = userEditedUnit
          ? unitPriceUsd != null && Number(linea.quantityM3) > 0
            ? Math.round(Number(linea.quantityM3) * Number(unitPriceUsd) * 100) / 100
            : null
          : linea.totalValueUsd;
        return { ...linea, unitPriceUsd, totalValueUsd };
      });
      const lineasOrdenadas = sortPtcMerchandiseLines(lineasEditables);
      const totalEditablesPtb = Math.round(
        lineasOrdenadas.reduce((sum, ln) => sum + (Number(ln.totalValueUsd) || 0), 0) * 100
      ) / 100;
      lineasMercanciaPtbEditablesRef.current = lineasOrdenadas;
      setLineasMercanciaPtbEditables(lineasOrdenadas);
      if (!valorUsdPtbEditadoRef.current && totalEditablesPtb > 0) {
        setValorUsdPtb(String(totalEditablesPtb));
      } else if (
        !valorUsdPtbEditadoRef.current &&
        totalEditablesPtb <= 0 &&
        merc.totalUsd > 0
      ) {
        setValorUsdPtb(String(merc.totalUsd));
      }
    },
    [
      invoiceCurrency,
      fechaEmbarque,
      numeroIdentificadorPtb,
      productDescriptionSnapshot,
      calidades,
      productos,
      categorias,
    ]
  );

  const aplicarDatosFormulario = useCallback((f, inv) => {
    const datos = f || {};
    if (inv?.empresa_emisor != null && inv.empresa_emisor !== '') {
      setEmpresaEmisor(Number(inv.empresa_emisor));
    } else if (datos.empresaEmisor != null && datos.empresaEmisor !== '') {
      setEmpresaEmisor(Number(datos.empresaEmisor));
    }
    const fechaDesdeDatos = String(datos.fechaEmbarque || datos.fechaInvoice || '').trim().slice(0, 10);
    const fechaDesdeFacturaEmitida =
      inv?.estado === 'factura' && inv?.fecha_invoice
        ? String(inv.fecha_invoice).slice(0, 10)
        : '';
    setFechaEmbarque(fechaDesdeDatos || fechaDesdeFacturaEmitida || '');
    if (datos.clienteNombre != null) setClienteBusqueda(datos.clienteNombre);
    else if (inv?.nombre_cliente) setClienteBusqueda(inv.nombre_cliente);
    if (datos.clienteDireccion != null) setClienteDireccion(datos.clienteDireccion);
    if (datos.taxId != null) setTaxId(datos.taxId);
    if (datos.pinCode != null) setPinCode(datos.pinCode);
    if (datos.paisOrigen != null) setPaisOrigen(datos.paisOrigen);
    if (datos.paisDestino != null) setPaisDestino(datos.paisDestino);
    else if (inv?.pais_destino) setPaisDestino(inv.pais_destino);
    if (datos.nombreNave != null) setNombreNave(datos.nombreNave);
    if (datos.puertoOrigen != null) setPuertoOrigen(datos.puertoOrigen);
    if (datos.puertoDestino != null) setPuertoDestino(datos.puertoDestino);
    if (datos.lugarEntrega != null) setLugarEntrega(datos.lugarEntrega);
    if (datos.nombreBl != null) setNombreBl(datos.nombreBl);
    else if (inv?.nombre_bl) setNombreBl(inv.nombre_bl);
    if (datos.contractNo != null) setContractNo(datos.contractNo);
    else if (inv?.contrato_pi) setContractNo(inv.contrato_pi);
    if (datos.idsProformaSeleccion?.length) {
      setIdsProformaSeleccion(datos.idsProformaSeleccion.map((id) => String(id)));
    }
    if (datos.purchaseOrder != null) {
      setPurchaseOrder(datos.purchaseOrder);
      if (String(datos.purchaseOrder).trim()) purchaseOrderEditadoRef.current = true;
    }
    if (datos.netWeight != null && datos.netWeight !== '') setNetWeight(String(datos.netWeight));
    if (datos.grossWeight != null && datos.grossWeight !== '') setGrossWeight(String(datos.grossWeight));
    if (datos.salesTerms != null) setSalesTerms(datos.salesTerms);
    if (datos.incoterms != null) setIncotermsTexto(datos.incoterms);
    if (datos.paymentTermsPtc != null) setPaymentTermsPtc(datos.paymentTermsPtc);
    if (datos.quantity != null) setQuantity(String(datos.quantity));
    if (datos.unidadVolumenPtc != null) {
      setUnidadVolumenPtc(String(datos.unidadVolumenPtc).toUpperCase() === 'CBM' ? 'CBM' : 'M3');
    }
    if (datos.contractNoPtb != null) setContractNoPtb(datos.contractNoPtb);
    if (datos.purchaseOrderPtb != null) {
      setPurchaseOrderPtb(datos.purchaseOrderPtb);
      const ptb = String(datos.purchaseOrderPtb).trim();
      if (ptb && ptb !== 'PTB-01') purchaseOrderPtbEditadoRef.current = true;
    }
    if (datos.termsOfSalePtb != null) setTermsOfSalePtb(datos.termsOfSalePtb);
    if (datos.clausePtb != null) setClausePtb(datos.clausePtb);
    if (datos.paymentTermsPtb != null) setPaymentTermsPtb(datos.paymentTermsPtb);
    if (datos.numeroIdentificadorPtc != null) {
      setNumeroIdentificadorPtc(invoiceNumberCorrelativo(datos.numeroIdentificadorPtc));
    }
    if (datos.numeroIdentificadorPtb != null) {
      setNumeroIdentificadorPtb(invoiceNumberCorrelativo(datos.numeroIdentificadorPtb));
    }
    if (datos.hsCodeSnapshot != null) setHsCodeSnapshot(datos.hsCodeSnapshot);
    if (datos.productDescriptionSnapshot != null) {
      setProductDescriptionSnapshot(datos.productDescriptionSnapshot);
    }
    if (datos.invoiceCurrency) setInvoiceCurrency(String(datos.invoiceCurrency).toUpperCase());
    const emisorDatos =
      inv?.empresa_emisor != null && inv.empresa_emisor !== ''
        ? Number(inv.empresa_emisor)
        : datos.empresaEmisor != null && datos.empresaEmisor !== ''
          ? Number(datos.empresaEmisor)
          : EMPRESA_PTC;
    const esPtbEmisorDatos = emisorDatos === EMPRESA_PTB;
    if (!esPtbEmisorDatos) {
      if (datos.totalUsd != null && datos.totalUsd !== '') setValorUsd(String(datos.totalUsd));
      else if (inv?.valor_usd != null) setValorUsd(String(inv.valor_usd));
      if ((datos.totalUsd != null && datos.totalUsd !== '') || inv?.valor_usd != null) {
        valorUsdEditadoRef.current = true;
      }
    }
    if (datos.totalUsdPtb != null && datos.totalUsdPtb !== '') {
      setValorUsdPtb(String(datos.totalUsdPtb));
      valorUsdPtbEditadoRef.current = true;
    } else if (esPtbEmisorDatos && datos.totalUsd != null && datos.totalUsd !== '') {
      setValorUsdPtb(String(datos.totalUsd));
      valorUsdPtbEditadoRef.current = true;
    }
    if (Array.isArray(datos.ajustesPtc)) setAjustesPtc(datos.ajustesPtc);
    if (datos.valorFobPtc != null) setValorFobPtc(String(datos.valorFobPtc));
    if (datos.valorFletePtc != null) setValorFletePtc(String(datos.valorFletePtc));
    if (datos.valorSeguroPtc != null) setValorSeguroPtc(String(datos.valorSeguroPtc));
    const textosMercaderia = pairFromInvoiceDatos(datos);
    setTextoMercaderiaPtcArriba(textosMercaderia.arriba);
    setTextoMercaderiaPtcAbajo(textosMercaderia.abajo);
    if (datos.totalM3 != null && datos.totalM3 !== '') setTotalM3(String(datos.totalM3));
    if (datos.totalBundles != null && datos.totalBundles !== '') {
      setTotalContenedores(String(datos.totalBundles));
    }
    if (Array.isArray(datos.lineasMercancia) && datos.lineasMercancia.length) {
      const lineasPtc = sortPtcMerchandiseLines(datos.lineasMercancia);
      lineasMercanciaEditablesRef.current = lineasPtc;
      setLineasMercanciaEditables(lineasPtc);
      mercPayloadRef.current = {
        lineasMercancia: lineasPtc,
        totalBundles: datos.totalBundles,
        totalM3: datos.totalM3,
        totalUsd: datos.totalUsd,
      };
    }
    if (Array.isArray(datos.lineasMercanciaPtb) && datos.lineasMercanciaPtb.length) {
      const lineasPtb = sortPtcMerchandiseLines(datos.lineasMercanciaPtb);
      lineasMercanciaPtbEditablesRef.current = lineasPtb;
      setLineasMercanciaPtbEditables(lineasPtb);
      if (!valorUsdPtbEditadoRef.current) {
        const sumPtb = Math.round(
          lineasPtb.reduce((sum, ln) => sum + (Number(ln.totalValueUsd) || 0), 0) * 100
        ) / 100;
        if (sumPtb > 0) setValorUsdPtb(String(sumPtb));
      }
    }
  }, []);

  const cargarInvoiceEdicion = useCallback(async (idInv, proformasLista) => {
      hydratingEditRef.current = true;
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${urlApiProd}/commercialinvoices/${idInv}`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveError(err.error || 'No se pudo cargar la invoice.');
        hydratingEditRef.current = false;
        return;
      }
      const inv = await res.json();
      const f = inv.datos_formulario || {};
      setIdPackingList(inv.id_packing_list);
      setBookingBusqueda(inv.nro_booking || '');
      setEstadoInvoice(inv.estado || 'prefactura');
      aplicarDatosFormulario(f, inv);
      if (inv.id_cliente != null) setIdCliente(String(inv.id_cliente));
      const plRes = await fetch(`${urlApiProd}/packinglists/${inv.id_packing_list}`, { headers });
      if (plRes.ok) {
        const pl = await plRes.json();
        detalleLineasRef.current = pl.lineas || [];
        const idsProformasActuales = (pl.id_proformas || []).map((id) => String(id));
        setIdsProformaSeleccion(idsProformasActuales);
        const contratosActuales = contractNoFromProformas(
          proformasLista?.length ? proformasLista : proformas,
          idsProformasActuales,
          clientes
        );
        setContractNo(contratosActuales);
        const listaPi = proformasLista?.length ? proformasLista : proformas;
        const pi =
          proformaFromLista(listaPi, idsProformasActuales, contratosActuales) ||
          null;
        idProformaUltimaRef.current = pi;
        const numeroVista =
          inv.estado === 'factura'
            ? invoiceNumberCorrelativo(f.numeroIdentificadorPtc || inv.numero_invoice)
            : numeroParaVistaPrevia(f.numeroIdentificadorPtc, inv.nro_booking);
        aplicarMercanciaDesdePl(pl, pi, numeroVista);
        aplicarMercanciaPtbDesdePl(pl, pi, numeroVista);
      }
      setErrors({});
      hydratingEditRef.current = false;
    },
    [aplicarDatosFormulario, aplicarMercanciaDesdePl, aplicarMercanciaPtbDesdePl, proformas]
  );

  const cargarDesdePackingList = useCallback(
    async (row) => {
      const token = localStorage.getItem('token');
      const res = await fetch(`${urlApiProd}/packinglists/${row.id_packing_list}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveError(err.error || 'No se pudo cargar el packing list.');
        return;
      }
      const pl = await res.json();
      setIdPackingList(pl.id_packing_list);
      setBookingBusqueda(pl.nro_booking || '');
      setFechaEmbarque(pl.fecha_embarque || '');
      setPaisOrigen(pl.pais_origen || '');
      setPaisDestino(pl.pais_destino || '');
      setNombreNave(pl.nombre_nave || '');
      setPuertoOrigen(pl.nombre_puerto_origen || '');
      setPuertoDestino(pl.nombre_puerto_destino || '');
      setLugarEntrega(pl.lugar_entrega || '');
      setNombreBl(pl.nombre_bl || '');
      setClausePtb(`FOB ${puertoNombreCorto(pl.nombre_puerto_origen)}`);
      const idsProformasPl = (pl.id_proformas || []).map((id) => String(id));
      setIdsProformaSeleccion(idsProformasPl);
      setContractNo(contractNoFromProformas(proformas, idsProformasPl, clientes));

      const lineas = pl.lineas || [];
      const gross = sumPesoGrossByMergeGroups(lineas);
      const net = sumPesoNetByMergeGroups(lineas);
      setGrossWeight(gross ? String(Math.round(gross * 1000) / 1000) : '');
      setNetWeight(net ? String(Math.round(net * 1000) / 1000) : '');
      const tc = pl.total_contenedores;
      const tm = pl.total_volumen_m3;
      setTotalContenedores(tc != null ? String(tc) : '');
      setTotalM3(
        tm != null
          ? String(tm)
          : lineas.length
            ? String(
                Math.round(
                  lineas.reduce((s, l) => s + (Number(l.total_m3) || 0), 0) * 1000
                ) / 1000
              )
            : ''
      );
      setHsCodeSnapshot(pl.hs_code_snapshot || '');
      setProductDescriptionSnapshot(pl.product_description_snapshot || '');

      const cli =
        pl.id_cliente != null
          ? clientes.find((c) => String(c.id_cliente) === String(pl.id_cliente))
          : null;
      if (cli) {
        setIdCliente(String(cli.id_cliente));
        setClienteBusqueda(cli.nombre || '');
        setTaxId(cli.tax ? String(cli.tax).trim() : '');
        setPinCode(cli.codigo_postal ? String(cli.codigo_postal).trim() : '');
        setClienteDireccion(
          buildClienteDireccionTexto(cli, pl.cliente_direccion_snapshot)
        );
        if (!hydratingEditRef.current) {
          const textosCliente = textosMercaderiaPtcFromCliente(cli);
          setTextoMercaderiaPtcArriba(textosCliente.arriba);
          setTextoMercaderiaPtcAbajo(textosCliente.abajo);
        }
      } else {
        setClienteBusqueda(pl.cliente_nombre_snapshot || '');
        setClienteDireccion(pl.cliente_direccion_snapshot || '');
        setPinCode(pl.cliente_codigo_postal ? String(pl.cliente_codigo_postal).trim() : '');
      }

      setNumeroIdentificadorPtc('');
      setNumeroIdentificadorPtb('');

      detalleLineasRef.current = lineas;
      let pi = proformaFromLista(
        proformas,
        (pl.id_proformas || []).map((id) => String(id)),
        ''
      );
      if (pl.id_proformas?.length) {
        if (!pi) {
          const ultima = pl.id_proformas[pl.id_proformas.length - 1];
          pi = proformas.find((p) => String(p.id_proforma) === String(ultima)) || null;
        }
        if (pi && !taxId && pi.cliente) {
          const cPi = clientes.find((c) => String(c.id_cliente) === String(pi.cliente));
          if (cPi?.tax) setTaxId(String(cPi.tax).trim());
        }
      }
      aplicarMercanciaDesdePl(pl, pi, numeroParaVistaPrevia('', pl.nro_booking));
      aplicarMercanciaPtbDesdePl(pl, pi, numeroParaVistaPrevia('', pl.nro_booking));
    },
    [clientes, proformas, aplicarMercanciaDesdePl, aplicarMercanciaPtbDesdePl]
  );

  const elegirBooking = (row) => {
    bookingElegidoRecienteRef.current = true;
    setBookingMenuOpen(false);
    setSaveError('');
    cargarDesdePackingList(row);
    window.setTimeout(() => {
      bookingElegidoRecienteRef.current = false;
    }, 400);
  };

  const onSelectCliente = (id) => {
    setIdCliente(id);
    const c = clientes.find((x) => String(x.id_cliente) === String(id));
    if (c) {
      setClienteBusqueda(c.nombre || '');
      setTaxId(c.tax ? String(c.tax).trim() : '');
      setPinCode(c.codigo_postal ? String(c.codigo_postal).trim() : '');
      setClienteDireccion(buildClienteDireccionTexto(c, ''));
      setPaisDestino(c.pais || '');
      setLugarEntrega(c.direccion_despacho || lugarEntrega);
      const textosCliente = textosMercaderiaPtcFromCliente(c);
      setTextoMercaderiaPtcArriba(textosCliente.arriba);
      setTextoMercaderiaPtcAbajo(textosCliente.abajo);
    }
  };


  useEffect(() => {
    if (idPackingList) return;
    setIdsProformaSeleccion((prev) =>
      prev.filter((id) =>
        proformasFiltradas.some((p) => String(p.id_proforma) === String(id))
      )
    );
  }, [idCliente, proformasFiltradas]);

  useEffect(() => {
    if (hydratingEditRef.current) return;
    const contratosLabel = contractNoFromProformas(proformas, idsProformaSeleccion, clientes);
    if (contratosLabel) {
      setContractNo(contratosLabel);
    } else if (!idsProformaSeleccion.length) {
      setContractNo('');
    }
    const pi = proformaFromLista(proformas, idsProformaSeleccion, contratosLabel);
    idProformaUltimaRef.current = pi;
    if (pi) {
      setIncotermsTexto(incotermDesdePi(pi, incoterms, puertos));
      setPaymentTermsPtc(paymentDesdePi(pi, paymentTerms));
    } else if (!idsProformaSeleccion.length) {
      setIncotermsTexto('');
      setPaymentTermsPtc('');
    }
    if (idsProformaSeleccion.length) {
      const poPi = purchaseOrderFromProformas(proformas, idsProformaSeleccion);
      if (!purchaseOrderEditadoRef.current) setPurchaseOrder(poPi);
      if (!purchaseOrderPtbEditadoRef.current) {
        setPurchaseOrderPtb(poPi || 'PTB-01');
      }
    } else if (!hydratingEditRef.current) {
      if (!purchaseOrderEditadoRef.current) setPurchaseOrder('');
      if (!purchaseOrderPtbEditadoRef.current) setPurchaseOrderPtb('PTB-01');
    }
    if (detalleLineasRef.current?.length) {
      aplicarMercanciaDesdePl(
        { lineas: detalleLineasRef.current, fecha_embarque: fechaEmbarque },
        pi
      );
      aplicarMercanciaPtbDesdePl(
        { lineas: detalleLineasRef.current, fecha_embarque: fechaEmbarque },
        pi
      );
    }
  }, [
    idsProformaSeleccion,
    proformas,
    clientes,
    incoterms,
    paymentTerms,
    puertos,
    fechaEmbarque,
    aplicarMercanciaDesdePl,
    aplicarMercanciaPtbDesdePl,
  ]);

  const onProformaBusquedaChange = (e) => {
    setProformaBusqueda(e.target.value);
    setProformaMenuOpen(true);
  };

  const elegirProformaLista = (pi) => {
    proformaElegidaRecienteRef.current = true;
    setIdsProformaSeleccion((prev) => appendProformaIdOrdenado(prev, pi.id_proforma));
    setProformaBusqueda('');
    setProformaMenuOpen(true);
    window.setTimeout(() => {
      proformaInputRef.current?.focus();
    }, 0);
    window.setTimeout(() => {
      proformaElegidaRecienteRef.current = false;
    }, 400);
  };

  const quitarProformaChip = (idProforma) => {
    setIdsProformaSeleccion((prev) => prev.filter((x) => String(x) !== String(idProforma)));
  };

  const onProformaInputBlur = () => {
    window.setTimeout(() => {
      if (proformaElegidaRecienteRef.current) {
        setProformaMenuOpen(true);
        return;
      }
      setProformaMenuOpen(false);
    }, 180);
  };

  useEffect(() => {
    if (!show) {
      invoiceModalLoadKeyRef.current = null;
      hydratingEditRef.current = false;
      return undefined;
    }

    const loadKey = String(invoiceEditId ?? 'new');
    if (invoiceModalLoadKeyRef.current === loadKey) return undefined;
    invoiceModalLoadKeyRef.current = loadKey;

    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setSaveError('');

    (async () => {
      let proformasCargadas = [];
      try {
        const [r1, r2, r3, r4, r5, rPl, rInv, rProd, rCat, rEsp, rCal] = await Promise.all([
          fetch(`${urlApiProd}/clientes`, { headers }),
          fetch(`${urlApiProd}/proformas`, { headers }),
          fetch(`${urlApiProd}/incoterms`, { headers }),
          fetch(`${urlApiProd}/paymentterms`, { headers }),
          fetch(`${urlApiProd}/puertos`, { headers }),
          fetch(`${urlApiProd}/packinglists`, { headers }),
          fetch(`${urlApiProd}/commercialinvoices`, { headers }),
          fetch(`${urlApiProd}/productos`, { headers }),
          fetch(`${urlApiProd}/categorias`, { headers }),
          fetch(`${urlApiProd}/especies`, { headers }),
          fetch(`${urlApiProd}/calidades`, { headers }),
        ]);
        if (cancelled) return;
        if (r1.ok) setClientes(await r1.json());
        if (r2.ok) {
          proformasCargadas = await r2.json();
          setProformas(proformasCargadas);
        }
        if (r3.ok) setIncoterms(await r3.json());
        if (r4.ok) setPaymentTerms(await r4.json());
        if (r5.ok) setPuertos(await r5.json());
        if (rProd.ok) setProductos(await rProd.json());
        if (rCat.ok) setCategorias(await rCat.json());
        if (rEsp.ok) setEspecies(await rEsp.json());
        if (rCal.ok) setCalidades(await rCal.json());
        const pl = rPl.ok ? await rPl.json() : [];
        const inv = rInv.ok ? await rInv.json() : [];
        const disponibles = packingListsSinInvoice(
          Array.isArray(pl) ? pl : [],
          Array.isArray(inv) ? inv : []
        );
        if (disponibles.length) {
          setPackingDisponiblesLocal(disponibles);
        } else if (Array.isArray(packingListDisponibles) && packingListDisponibles.length) {
          setPackingDisponiblesLocal(packingListDisponibles);
        } else {
          setPackingDisponiblesLocal([]);
        }
        if (invoiceEditId) {
          await cargarInvoiceEdicion(invoiceEditId, proformasCargadas);
        } else {
          resetForm();
        }
      } catch (e) {
        if (!cancelled) console.error('Error cargando datos invoice:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- una carga por apertura (loadKey)
  }, [show, invoiceEditId]);

  useEffect(() => {
    if (hydratingEditRef.current || loading) return;
    if (!detalleLineasRef.current?.length) return;
    const plMini = { lineas: detalleLineasRef.current, fecha_embarque: fechaEmbarque };
    aplicarMercanciaDesdePl(plMini, idProformaUltimaRef.current);
    aplicarMercanciaPtbDesdePl(plMini, idProformaUltimaRef.current);
  }, [invoiceCurrency, aplicarMercanciaDesdePl, aplicarMercanciaPtbDesdePl, fechaEmbarque, loading]);

  const validate = () => {
    const e = {};
    const esPtc = empresaEmisor === EMPRESA_PTC;

    if (!idPackingList && !bookingBusqueda.trim()) {
      e.booking = 'Seleccione un N° Booking con packing list.';
    }
    if (!isEditMode && !idPackingList) {
      e.booking = 'Seleccione un booking con packing list sin invoice.';
    }
    if (!detalleLineasRef.current?.length) {
      e.detalle = 'El packing list no tiene líneas de detalle.';
    }

    if (!textoRequerido(clienteBusqueda)) e.cliente = 'Indique el cliente.';
    if (!textoRequerido(clienteDireccion)) {
      e.clienteDireccion = 'Indique la dirección del cliente.';
    }
    if (!textoRequerido(taxId)) e.taxId = 'Indique el TAX ID.';
    if (!textoRequerido(pinCode)) e.pinCode = 'Indique el PIN CODE.';
    if (!textoRequerido(paisOrigen)) e.paisOrigen = 'Indique el país de origen.';
    if (!textoRequerido(paisDestino)) e.paisDestino = 'Indique el país de destino.';
    if (!textoRequerido(nombreNave)) e.nombreNave = 'Indique el nombre de la nave.';
    if (!textoRequerido(puertoOrigen)) e.puertoOrigen = 'Indique el puerto de origen.';
    if (!textoRequerido(puertoDestino)) e.puertoDestino = 'Indique el puerto de destino.';
    if (!textoRequerido(lugarEntrega)) e.lugarEntrega = 'Indique el lugar de entrega.';
    if (!textoRequerido(nombreBl)) e.nombreBl = 'Ingrese el nombre del BL.';
    if (!numeroRequerido(netWeight)) e.netWeight = 'Net Weight requerido (desde booking).';
    if (!numeroRequerido(grossWeight)) e.grossWeight = 'Gross Weight requerido (desde booking).';
    if (!textoRequerido(salesTerms)) e.salesTerms = 'Indique Sales terms.';
    if (!textoRequerido(incotermsTexto)) e.incotermsTexto = 'Indique Incoterms.';
    if (!textoRequerido(paymentTermsPtc)) e.paymentTermsPtc = 'Indique Payment terms (PI).';
    if (!numeroRequerido(quantity)) {
      e.quantity = 'Indique Quantity (total volumen del packing list).';
    }
    if (!textoRequerido(invoiceCurrency)) {
      e.invoiceCurrency = 'Indique la moneda de la invoice.';
    }

    const lineasPtc = lineasMercanciaEditablesRef.current || [];
    const lineasPtb = lineasMercanciaPtbEditablesRef.current || [];

    if (esPtc) {
      if (!numeroRequerido(valorUsd)) {
        e.valorUsd = 'Indique el valor total en USD (PTC).';
      }
      if (!numeroRequerido(valorUsdPtb)) {
        e.valorUsdPtb = 'Indique el valor total en USD (PTB).';
      }
      if (lineasPtc.length > 0 && mercanciaLineasSinPrecioUnitario(lineasPtc)) {
        e.mercanciaPtc = 'Complete el precio unitario USD/M³ en todas las líneas PTC con M³.';
      }
      if (lineasPtb.length > 0 && mercanciaLineasSinPrecioUnitario(lineasPtb)) {
        e.mercanciaPtb = 'Complete el precio unitario USD/M³ en todas las líneas PTB con M³.';
      }
      if (!textoRequerido(termsOfSalePtb)) {
        e.termsOfSalePtb = 'Indique Terms of Sale (PTB).';
      }
      if (!textoRequerido(clausePtb)) e.clausePtb = 'Indique Clause (PTB).';
      if (!textoRequerido(paymentTermsPtb)) {
        e.paymentTermsPtb = 'Indique Payment terms (PTB).';
      }
      if (ajustesPtc.length > 0) {
        const ajusteIncompleto = ajustesPtc.some(
          (a) => !textoRequerido(a.descripcion) || !numeroRequerido(a.valor)
        );
        if (ajusteIncompleto) {
          e.ajustesPtc = 'Complete descripción y valor en cada ajuste PTC agregado.';
        }
      }
    } else if (!numeroRequerido(valorUsdPtb)) {
      e.valorUsdPtb = 'Indique el valor total en USD (PTB).';
    }
    if (!esPtc && lineasPtb.length > 0 && mercanciaLineasSinPrecioUnitario(lineasPtb)) {
      e.mercanciaPtb = 'Complete el precio unitario USD/M³ en todas las líneas PTB con M³.';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildFormPayload = () => ({
    empresaEmisor,
    fechaEmbarque,
    fechaInvoice: fechaEmbarque,
    clienteNombre: clienteBusqueda,
    clienteDireccion,
    taxId,
    pinCode,
    paisOrigen,
    paisDestino,
    nombreNave,
    puertoOrigen,
    puertoDestino,
    lugarEntrega,
    nombreBl,
    contractNo: contractNoFromProformas(proformas, idsProformaSeleccion, clientes) || contractNo,
    idsProformaSeleccion,
    purchaseOrder,
    netWeight: parseFloat(netWeight),
    grossWeight: parseFloat(grossWeight),
    salesTerms,
    incoterms: incotermsTexto,
    paymentTermsPtc,
    quantity,
    unidadVolumenPtc,
    contractNoPtb,
    purchaseOrderPtb,
    termsOfSalePtb,
    clausePtb,
    paymentTermsPtb,
    numeroIdentificadorPtc,
    numeroIdentificadorPtb,
    textoEmpresaPtc,
    textoEmpresaPtb,
    hsCodeSnapshot,
    productDescriptionSnapshot,
    invoiceCurrency: (invoiceCurrency || 'USD').trim().toUpperCase(),
    bookingBusqueda,
    nroBooking: bookingBusqueda,
    lineasMercancia: lineasMercanciaEditablesRef.current || [],
    lineasMercanciaPtb: lineasMercanciaPtbEditablesRef.current || [],
    calidades: (calidades || []).map((c) => ({
      id_calidad: c.id_calidad,
      calidad: c.calidad,
    })),
    totalBundles: mercPayloadRef.current?.totalBundles,
    totalM3: mercPayloadRef.current?.totalM3,
    totalUsd: valorUsd === '' ? null : parseFloat(valorUsd),
    totalUsdPtb: valorUsdPtb === '' ? null : parseFloat(valorUsdPtb),
    valorFinalUsd: valorFinalPtc,
    ajustesPtc,
    valorFobPtc: valorFobPtc === '' ? null : parseFloat(valorFobPtc),
    valorFletePtc: valorFletePtc === '' ? null : parseFloat(valorFletePtc),
    valorSeguroPtc: valorSeguroPtc === '' ? null : parseFloat(valorSeguroPtc),
    ...textosMercaderiaPtcPayloadFromPair({
      arriba: textoMercaderiaPtcArriba,
      abajo: textoMercaderiaPtcAbajo,
    }),
  });

  const totalAjustesPtc = ajustesPtc.reduce((sum, ajuste) => {
    const valor = Number(ajuste.valor);
    if (!Number.isFinite(valor)) return sum;
    return sum + (ajuste.tipo === 'descuento' ? -valor : valor);
  }, 0);
  const totalCostosComposicionPtc = [valorFobPtc, valorFletePtc, valorSeguroPtc].reduce(
    (sum, value) => sum + (value !== '' && Number.isFinite(Number(value)) ? Number(value) : 0),
    0
  );
  const valorFinalPtc =
    Number.isFinite(Number(valorUsd))
      ? Math.round((Number(valorUsd) + totalAjustesPtc + totalCostosComposicionPtc) * 100) / 100
      : null;

  const cambiarPrecioUnitario = (index, value) => {
    const lineas = lineasMercanciaEditablesRef.current.map((linea, i) => {
      if (i !== index) return linea;
      const unitPriceUsd = value === '' ? null : Number(value);
      const totalValueUsd =
        unitPriceUsd != null && Number.isFinite(unitPriceUsd) && Number(linea.quantityM3) > 0
          ? Math.round(Number(linea.quantityM3) * unitPriceUsd * 100) / 100
          : null;
      return { ...linea, unitPriceUsd, totalValueUsd };
    });
    lineasMercanciaEditablesRef.current = lineas;
    setLineasMercanciaEditables(lineas);
    // Al corregir unitarios, siempre recalcular VALOR TOTAL = suma de todas las líneas.
    const total = lineas.reduce(
      (sum, linea) => sum + (Number(linea.totalValueUsd) || 0),
      0
    );
    const totalRound = Math.round(total * 100) / 100;
    valorUsdEditadoRef.current = false;
    setValorUsd(totalRound > 0 ? String(totalRound) : '');
    if (mercPayloadRef.current) {
      mercPayloadRef.current = {
        ...mercPayloadRef.current,
        lineasMercancia: lineas,
        totalUsd: totalRound > 0 ? totalRound : null,
      };
    }
  };

  const cambiarPrecioUnitarioPtb = (index, value) => {
    const lineas = lineasMercanciaPtbEditablesRef.current.map((linea, i) => {
      if (i !== index) return linea;
      const unitPriceUsd = value === '' ? null : Number(value);
      const totalValueUsd =
        unitPriceUsd != null && Number.isFinite(unitPriceUsd) && Number(linea.quantityM3) > 0
          ? Math.round(Number(linea.quantityM3) * unitPriceUsd * 100) / 100
          : null;
      return { ...linea, unitPriceUsd, totalValueUsd };
    });
    lineasMercanciaPtbEditablesRef.current = lineas;
    setLineasMercanciaPtbEditables(lineas);
    const total = lineas.reduce(
      (sum, linea) => sum + (Number(linea.totalValueUsd) || 0),
      0
    );
    const totalRound = Math.round(total * 100) / 100;
    valorUsdPtbEditadoRef.current = false;
    setValorUsdPtb(totalRound > 0 ? String(totalRound) : '');
  };

  const cerrarPreview = useCallback(() => {
    setPreviewUrls((prev) => {
      revokePreviewUrls(prev);
      return { PTC: null, PTB: null };
    });
    setPreviewVariant('PTC');
    setShowPreview(false);
    setPreviewError('');
    setPreviewWarning('');
    previewParamsRef.current = null;
  }, [revokePreviewUrls]);

  const validatePreview = () => validate();

  const resolverPiParaPrecios = useCallback(() => {
    return (
      idProformaUltimaRef.current ||
      proformaFromLista(proformas, idsProformaSeleccion, contractNo) ||
      null
    );
  }, [proformas, idsProformaSeleccion, contractNo]);

  const buildPtbPreviewParams = async (
    datosFormulario,
    { numeroInvoice, piResolved, valorUsdPtbOverride = null }
  ) => {
    const overridesPtb = {
      numeroInvoice,
      proformasLista: proformas,
      calidades,
      productos,
      categorias,
      ...(valorUsdPtbOverride != null ? { valorUsd: valorUsdPtbOverride } : {}),
    };
    return invoicePtbParamsFromFormWithPackingList(
      datosFormulario,
      overridesPtb,
      {
        proformaEnMemoria: piResolved,
        plLineas: detalleLineasRef.current,
      }
    );
  };

  const handleVistaPrevia = async () => {
    setPreviewError('');
    setPreviewWarning('');
    if (!validatePreview()) return;
    const esPtb = empresaEmisor === EMPRESA_PTB;
    const numeroPreview = numeroParaVistaPrevia(
      esPtb ? numeroIdentificadorPtb : numeroIdentificadorPtc,
      bookingBusqueda
    );
    const piResolved = resolverPiParaPrecios();
    if (!esPtb && !piResolved) {
      setPreviewError(
        'Seleccione la PI (Contract No.) en el formulario. Los precios UNIT PRICE / us$/M³ del PDF PTC salen de la PI vinculada.'
      );
      return;
    }
    if (detalleLineasRef.current?.length) {
      const plMini = {
        lineas: detalleLineasRef.current,
        fecha_embarque: fechaEmbarque,
        product_description_snapshot: productDescriptionSnapshot,
      };
      aplicarMercanciaDesdePl(plMini, piResolved, numeroPreview);
      aplicarMercanciaPtbDesdePl(plMini, piResolved, numeroPreview);
    }
    setPreviewLoading(true);
    setShowPreview(true);
    setPreviewUrls((prev) => {
      revokePreviewUrls(prev);
      return { PTC: null, PTB: null };
    });
    setPreviewVariant(esPtb ? 'PTB' : 'PTC');
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : null;
    try {
      const datosFormulario = buildFormPayload();
      const valor = parseFloat(valorUsd);
      const valorOpt = Number.isFinite(valor) ? valor : null;
      const valorPtb = parseFloat(valorUsdPtb);
      const valorPtbOpt = Number.isFinite(valorPtb) ? valorPtb : null;
      if (esPtb) {
        const paramsPtb = await buildPtbPreviewParams(datosFormulario, {
          numeroInvoice: numeroIdentificadorPtb.trim(),
          piResolved,
          valorUsdPtbOverride: valorPtbOpt,
        });
        if (!lineasPdfTienenPrecios(paramsPtb?.lineas)) {
          setPreviewWarning(mensajePtbSinPreciosLogistico(detalleLineasRef.current));
        }
        previewParamsRef.current = { PTC: null, PTB: paramsPtb };
        setPreviewUrls({
          PTC: null,
          PTB: createInvoicePtbPdfBlobUrl(paramsPtb),
        });
      } else {
        const piPricingError = mensajePtcPiPricingError(
          detalleLineasRef.current,
          proformas,
          idsProformaSeleccion,
          calidades,
          clientes
        );
        if (piPricingError) {
          setPreviewError(piPricingError);
          previewParamsRef.current = null;
          setPreviewUrls({ PTC: null, PTB: null });
          return;
        }
        const overridesPtc = {
          numeroInvoice: numeroIdentificadorPtc.trim(),
          valorUsd: valorOpt,
          proformasLista: proformas,
          idProformas: idsProformaSeleccion,
          calidades,
          clientes,
        };
        let paramsPtc = invoicePtcParamsFromFormAndDetail(
          datosFormulario,
          detalleLineasRef.current,
          piResolved,
          overridesPtc
        );
        if (idPackingList && headers) {
          paramsPtc = await invoicePtcParamsFromFormWithPackingList(
            datosFormulario,
            overridesPtc,
            {
              apiBase: urlApiProd,
              headers,
              idPackingList,
              proformaEnMemoria: piResolved,
              plLineas: detalleLineasRef.current,
            }
          );
        }
        if (!lineasPdfTienenPrecios(paramsPtc?.lineas)) {
          setPreviewError(
            mensajePtcPiPricingError(
              detalleLineasRef.current,
              proformas,
              idsProformaSeleccion,
              calidades,
              clientes
            ) ||
              'La PI del Contract No. no tiene USD/m³ (ni USD total por línea) para cruzar con el packing list. Abra la PI en Proformas y complete USD/m³ o USD total y M³ por producto.'
          );
          previewParamsRef.current = null;
          setPreviewUrls({ PTC: null, PTB: null });
          return;
        }
        const paramsPtb = await buildPtbPreviewParams(datosFormulario, {
          numeroInvoice: numeroIdentificadorPtb.trim(),
          piResolved,
          valorUsdPtbOverride: valorPtbOpt,
        });
        let ptbUrl = createInvoicePtbPdfBlobUrl(paramsPtb);
        if (!lineasPdfTienenPrecios(paramsPtb?.lineas)) {
          setPreviewWarning(mensajePtbSinPreciosLogistico(detalleLineasRef.current));
        }
        previewParamsRef.current = { PTC: paramsPtc, PTB: paramsPtb };
        setPreviewUrls({
          PTC: createInvoicePtcPdfBlobUrl(paramsPtc),
          PTB: ptbUrl,
        });
      }
    } catch (err) {
      setPreviewError(err.message || 'No se pudo generar la vista previa.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDescargarPreview = () => {
    const ref = previewParamsRef.current;
    if (!ref) return;
    if (previewVariant === 'PTB') {
      if (ref.PTB) generateInvoicePtbPdf(ref.PTB);
    } else {
      if (ref.PTC) generateInvoicePtcPdf(ref.PTC);
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaveError('');
    setSaving(true);
    const identificadorForm = empresaEmisor === EMPRESA_PTB ? numeroIdentificadorPtb : numeroIdentificadorPtc;
    const numeroVista = numeroParaVistaPrevia(identificadorForm, bookingBusqueda);
    const numeroGuardar =
      estadoInvoice === 'factura'
        ? invoiceNumberCorrelativo(identificadorForm)
        : '';
    if (detalleLineasRef.current?.length) {
      const plMini = {
        lineas: detalleLineasRef.current,
        fecha_embarque: fechaEmbarque,
      };
      aplicarMercanciaDesdePl(plMini, idProformaUltimaRef.current, numeroVista);
      aplicarMercanciaPtbDesdePl(plMini, idProformaUltimaRef.current, numeroVista);
    }
    const datosFormulario = buildFormPayload();
    try {
      const token = localStorage.getItem('token');
      const payloadBase = {
        numero_invoice: numeroGuardar,
        fecha_invoice: fechaEmbarque,
        empresa_emisor: empresaEmisor,
        nombre_bl: nombreBl.trim(),
        nombre_cliente: clienteBusqueda.trim(),
        pais_destino: paisDestino,
        valor_usd: valorFinalPtc,
        contrato_pi:
          contractNoFromProformas(proformas, idsProformaSeleccion, clientes).trim() ||
          contractNo.trim(),
        total_contenedores: totalContenedores === '' ? null : parseFloat(totalContenedores),
        total_m3: totalM3 === '' ? null : parseFloat(totalM3),
        datos_formulario: datosFormulario,
      };
      const url = isEditMode
        ? `${urlApiProd}/commercialinvoices/${invoiceEditId}`
        : `${urlApiProd}/commercialinvoices`;
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          isEditMode
            ? payloadBase
            : {
                ...payloadBase,
                id_packing_list: idPackingList,
                fecha_embarque: fechaEmbarque,
                id_cliente: idCliente ? parseInt(idCliente, 10) : null,
              }
        ),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(body.error || `Error ${res.status}`);
        return;
      }
      notificar.exito(
        isEditMode ? 'Invoice editada correctamente' : 'Prefactura creada exitosamente'
      );
      if (typeof onGuardarExito === 'function') onGuardarExito();
      cerrar();
    } catch (e) {
      setSaveError(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const mostrarBloquePtb = empresaEmisor === EMPRESA_PTC;
  const esFacturaEmitida = estadoInvoice === 'factura';
  const previewUrlActual = previewVariant === 'PTB' ? previewUrls.PTB : previewUrls.PTC;
  const previewTitle =
    empresaEmisor === EMPRESA_PTB
      ? 'Vista previa — Invoice PTB'
      : `Vista previa — Invoice ${previewVariant}`;
  const previewIframeTitle =
    empresaEmisor === EMPRESA_PTB
      ? 'Vista previa Invoice PTB'
      : `Vista previa Invoice ${previewVariant}`;

  const onValorUsdChange = (e) => {
    valorUsdEditadoRef.current = true;
    setValorUsd(e.target.value);
  };

  const onValorUsdPtbChange = (e) => {
    valorUsdPtbEditadoRef.current = true;
    setValorUsdPtb(e.target.value);
  };

  const ptcValorTotalUsdControl = (size) => (
    <>
      <Form.Control
        type="number"
        step="0.01"
        min="0"
        {...(size ? { size } : {})}
        value={valorUsd}
        onChange={onValorUsdChange}
        isInvalid={!!errors.valorUsd}
      />
      <Form.Control.Feedback type="invalid">{errors.valorUsd}</Form.Control.Feedback>
    </>
  );

  const ptcMercanciaTableValorTotalFooter = (
    <tfoot>
      <tr>
        <td colSpan={6} className="text-end align-middle bg-light">
          <span className="fw-semibold">Valor total USD (PTC)</span>
        </td>
        <td className="bg-light align-middle">{ptcValorTotalUsdControl('sm')}</td>
      </tr>
    </tfoot>
  );

  const ptcValorTotalUsdSinTabla = (
    <Row className="mb-3">
      <Col md={4}>
        <Form.Group>
          <Form.Label>Valor total USD (PTC)</Form.Label>
          {ptcValorTotalUsdControl()}
        </Form.Group>
      </Col>
    </Row>
  );

  const ptbValorTotalUsdControl = (size) => (
    <>
      <Form.Control
        type="number"
        step="0.01"
        min="0"
        {...(size ? { size } : {})}
        value={valorUsdPtb}
        onChange={onValorUsdPtbChange}
        isInvalid={!!errors.valorUsdPtb}
      />
      <Form.Control.Feedback type="invalid">{errors.valorUsdPtb}</Form.Control.Feedback>
    </>
  );

  const ptbMercanciaTableValorTotalFooter = (
    <tfoot>
      <tr>
        <td colSpan={6} className="text-end align-middle bg-light">
          <span className="fw-semibold">Valor total USD (PTB)</span>
        </td>
        <td className="bg-light align-middle">{ptbValorTotalUsdControl('sm')}</td>
      </tr>
    </tfoot>
  );

  const ptbValorTotalUsdSinTabla = (
    <Row className="mb-3">
      <Col md={4}>
        <Form.Group>
          <Form.Label>Valor total USD (PTB)</Form.Label>
          {ptbValorTotalUsdControl()}
        </Form.Group>
      </Col>
    </Row>
  );

  return (
    <>
    <ElegantModal
      show={show}
      onClose={cerrar}
      dismissable={false}
      title={
        isEditMode
          ? estadoInvoice === 'factura'
            ? 'Editar invoice'
            : 'Editar prefactura'
          : 'Crear prefactura'
      }
      subtitle={
        isEditMode
          ? estadoInvoice === 'factura'
            ? 'Modifique los datos de la factura. El booking no se puede cambiar.'
            : 'Modifique los datos de la prefactura. El booking no se puede cambiar.'
          : 'Seleccione empresa y booking con packing list. Los campos se completan desde el booking y pueden editarse.'
      }
      titleId="invoice-create-title"
      size="xl"
      footer={
        loading ? null : (
          <div className="d-flex gap-2 justify-content-end flex-wrap align-items-center">
            {Object.keys(errors).length > 0 ? (
              <span className="text-danger small me-1">
                Debe revisar los campos con error.
              </span>
            ) : null}
            <Button variant="secondary" type="button" onClick={cerrar} disabled={saving || previewLoading}>
              Cancelar
            </Button>
            <Button
              variant="outline-primary"
              type="button"
              onClick={handleVistaPrevia}
              disabled={saving || previewLoading}
            >
              {previewLoading ? 'Generando…' : 'Vista previa'}
            </Button>
            <Button variant="primary" type="submit" form="invoice-form-add" disabled={saving || previewLoading}>
              {saving ? 'Guardando…' : isEditMode ? 'Guardar cambios' : 'Guardar prefactura'}
            </Button>
          </div>
        )
      }
    >
      <form id="invoice-form-add" onSubmit={handleSubmit}>
        {loading ? (
          <p className="text-center text-muted mb-0">Cargando datos…</p>
        ) : (
          <>
            {saveError ? (
              <Alert variant="danger" className="mb-3" dismissible onClose={() => setSaveError('')}>
                {saveError}
              </Alert>
            ) : null}

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Empresa emisora</Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    <Form.Check
                      type="radio"
                      id="inv-empresa-ptc"
                      name="empresaInvoice"
                      label="PTC — Guarda Invoice PTC + Invoice PTB"
                      checked={empresaEmisor === EMPRESA_PTC}
                      onChange={() => setEmpresaEmisor(EMPRESA_PTC)}
                    />
                    <Form.Check
                      type="radio"
                      id="inv-empresa-ptb"
                      name="empresaInvoice"
                      label="PTB — Guarda solo Invoice PTB (campos PTC)"
                      checked={empresaEmisor === EMPRESA_PTB}
                      onChange={() => setEmpresaEmisor(EMPRESA_PTB)}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <div ref={bookingAnchorRef} className="position-relative">
                    <Form.Label>N° Booking</Form.Label>
                    <Form.Control
                      type="text"
                      autoComplete="off"
                      readOnly={isEditMode}
                      placeholder={
                        isEditMode ? 'Booking vinculado' : 'Buscar booking con packing list…'
                      }
                      value={bookingBusqueda}
                      onChange={(e) => {
                        if (isEditMode) return;
                        setBookingBusqueda(e.target.value);
                        setBookingMenuOpen(true);
                        setIdPackingList(null);
                      }}
                      onFocus={() => {
                        if (!isEditMode) setBookingMenuOpen(true);
                      }}
                      onBlur={() => {
                        window.setTimeout(() => {
                          if (!bookingElegidoRecienteRef.current) setBookingMenuOpen(false);
                        }, 180);
                      }}
                      isInvalid={!!errors.booking}
                    />
                    <BookingAutocompletePortal
                      open={!isEditMode && bookingMenuOpen}
                      anchorRef={bookingAnchorRef}
                    >
                      {bookingsOpciones.length > 0 ? (
                        bookingsOpciones.map((r) => (
                          <ListGroup.Item
                            key={r.id_packing_list}
                            action
                            className="py-2 px-3"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              elegirBooking(r);
                            }}
                          >
                            {r.nro_booking}
                          </ListGroup.Item>
                        ))
                      ) : (
                        <ListGroup.Item disabled className="py-2 px-3 small text-muted">
                          {packingDisponiblesLista.length === 0
                            ? 'No hay packing lists sin invoice'
                            : 'Sin coincidencias para la búsqueda'}
                        </ListGroup.Item>
                      )}
                    </BookingAutocompletePortal>
                    <Form.Text className="text-muted">
                      Solo packing lists sin invoice asociada.
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">{errors.booking}</Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Fecha embarque</Form.Label>
                  <DateInput
                    value={fechaEmbarque}
                    onChange={(e) => setFechaEmbarque(e.target.value)}
                    isInvalid={!!errors.fechaEmbarque}
                  />
                  <Form.Control.Feedback type="invalid">{errors.fechaEmbarque}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {errors.detalle ? (
              <Alert variant="danger" className="py-2 mb-3">{errors.detalle}</Alert>
            ) : null}

            {empresaEmisor === EMPRESA_PTC && lineasMercanciaEditables.length > 0 ? (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label>Precios mercancía Invoice PTC (desde PI)</Form.Label>
                  {errors.mercanciaPtc ? (
                    <Alert variant="danger" className="py-2 mb-2">{errors.mercanciaPtc}</Alert>
                  ) : null}
                  <Table bordered hover responsive size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Grado</th>
                        <th>Pcs/paq</th>
                        <th>Medida</th>
                        <th>M³</th>
                        <th>Precio unitario USD/M³</th>
                        <th>Total USD línea</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineasMercanciaEditables.map((linea, index) => (
                        <tr key={`ptc-${productMeasureGradeKey(linea)}-${index}`}>
                          <td>{linea.productName || '—'}</td>
                          <td>{linea.grado || '—'}</td>
                          <td>{formatPcsPaqInvoiceCell(linea)}</td>
                          <td>
                            {[linea.espessura, linea.largura, linea.comprimento]
                              .filter(Boolean)
                              .join(' × ') || '—'}
                          </td>
                          <td>{linea.quantityM3 ?? '—'}</td>
                          <td style={{ minWidth: 190 }}>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={linea.unitPriceUsd ?? ''}
                              onChange={(e) => cambiarPrecioUnitario(index, e.target.value)}
                            />
                          </td>
                          <td>{linea.totalValueUsd ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    {ptcMercanciaTableValorTotalFooter}
                  </Table>
                </Col>
              </Row>
            ) : null}
            {empresaEmisor === EMPRESA_PTC && lineasMercanciaEditables.length === 0
              ? ptcValorTotalUsdSinTabla
              : null}

            {empresaEmisor === EMPRESA_PTB && lineasMercanciaPtbEditables.length > 0 ? (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label>Precios mercancía Invoice PTB (desde Excel packing list)</Form.Label>
                  {errors.mercanciaPtb ? (
                    <Alert variant="danger" className="py-2 mb-2">{errors.mercanciaPtb}</Alert>
                  ) : null}
                  <Table bordered hover responsive size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Grado</th>
                        <th>Pcs/paq</th>
                        <th>Medida</th>
                        <th>M³</th>
                        <th>Precio unitario USD/M³</th>
                        <th>Total USD línea</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineasMercanciaPtbEditables.map((linea, index) => (
                        <tr key={`ptb-main-${productMeasureGradeKey(linea)}-${index}`}>
                          <td>{linea.productName || '—'}</td>
                          <td>{linea.grado || '—'}</td>
                          <td>{formatPcsPaqInvoiceCell(linea)}</td>
                          <td>
                            {[linea.espessura, linea.largura, linea.comprimento]
                              .filter(Boolean)
                              .join(' × ') || '—'}
                          </td>
                          <td>{linea.quantityM3 ?? '—'}</td>
                          <td style={{ minWidth: 190 }}>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={linea.unitPriceUsd ?? ''}
                              onChange={(e) => cambiarPrecioUnitarioPtb(index, e.target.value)}
                            />
                          </td>
                          <td>{linea.totalValueUsd ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    {ptbMercanciaTableValorTotalFooter}
                  </Table>
                  <Form.Text className="text-muted">
                    Precio propuesto desde el Excel; puede ajustarlo antes de emitir el PDF PTB.
                  </Form.Text>
                </Col>
              </Row>
            ) : null}
            {empresaEmisor === EMPRESA_PTB && lineasMercanciaPtbEditables.length === 0
              ? ptbValorTotalUsdSinTabla
              : null}

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Moneda invoice (Currency)</Form.Label>
                  <Form.Control
                    value={invoiceCurrency}
                    onChange={(e) => setInvoiceCurrency(e.target.value.toUpperCase())}
                    placeholder="USD"
                    maxLength={8}
                    isInvalid={!!errors.invoiceCurrency}
                  />
                  <Form.Control.Feedback type="invalid">{errors.invoiceCurrency}</Form.Control.Feedback>
                  <Form.Text className="text-muted">Por defecto USD</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {empresaEmisor === EMPRESA_PTC ? (
              <>
                <Row className="mb-2">
                  <Col md={12}>
                    <Form.Label>Ajustes sobre valor de mercadería PTC</Form.Label>
                    {errors.ajustesPtc ? (
                      <Alert variant="danger" className="py-2 mt-1 mb-0">{errors.ajustesPtc}</Alert>
                    ) : null}
                  </Col>
                </Row>
                {ajustesPtc.map((ajuste, index) => (
                  <Row className="mb-2" key={`ajuste-ptc-${index}`}>
                    <Col md={5}>
                      <Form.Control
                        placeholder="Descripción"
                        value={ajuste.descripcion}
                        onChange={(e) => setAjustesPtc((prev) => prev.map((item, i) => i === index ? { ...item, descripcion: e.target.value } : item))}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Select
                        value={ajuste.tipo}
                        onChange={(e) => setAjustesPtc((prev) => prev.map((item, i) => i === index ? { ...item, tipo: e.target.value } : item))}
                      >
                        <option value="cargo">Cargo</option>
                        <option value="descuento">Descuento</option>
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Valor USD/EUR"
                        value={ajuste.valor}
                        onChange={(e) => setAjustesPtc((prev) => prev.map((item, i) => i === index ? { ...item, valor: e.target.value } : item))}
                      />
                    </Col>
                    <Col md={1}>
                      <Button type="button" variant="outline-danger" onClick={() => setAjustesPtc((prev) => prev.filter((_, i) => i !== index))}>×</Button>
                    </Col>
                  </Row>
                ))}
                <Button
                  type="button"
                  variant="outline-primary"
                  className="mb-3"
                  onClick={() => setAjustesPtc((prev) => [...prev, { descripcion: '', tipo: 'cargo', valor: '' }])}
                >
                  + Agregar cargo/descuento
                </Button>
                <Row className="mb-3">
                  <Col md={4}><Form.Label>Valor FOB</Form.Label><Form.Control type="number" step="0.01" value={valorFobPtc} onChange={(e) => setValorFobPtc(e.target.value)} /></Col>
                  <Col md={4}><Form.Label>Valor Flete</Form.Label><Form.Control type="number" step="0.01" value={valorFletePtc} onChange={(e) => setValorFletePtc(e.target.value)} /></Col>
                  <Col md={4}><Form.Label>Valor Seguro</Form.Label><Form.Control type="number" step="0.01" value={valorSeguroPtc} onChange={(e) => setValorSeguroPtc(e.target.value)} /></Col>
                </Row>
                <Alert variant="light" className="py-2">
                  <strong>{totalInvoiceLabel(invoiceCurrency)}:</strong>{' '}
                  {valorFinalPtc != null ? valorFinalPtc.toFixed(2) : '—'}
                </Alert>
                <TextosMercaderiaPtcFields
                  arriba={textoMercaderiaPtcArriba}
                  abajo={textoMercaderiaPtcAbajo}
                  onChangeArriba={(value) => setTextoMercaderiaPtcArriba(truncateTextoMercaderiaPtc(value))}
                  onChangeAbajo={(value) => setTextoMercaderiaPtcAbajo(truncateTextoMercaderiaPtc(value))}
                />
              </>
            ) : null}

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="position-relative">
                        <Form.Label>Cliente / Customer (nombre del cliente)</Form.Label>
                  <Form.Control
                    type="text"
                    autoComplete="off"
                    value={clienteBusqueda}
                    onChange={(e) => {
                      setClienteBusqueda(e.target.value);
                      setClienteMenuOpen(true);
                    }}
                    onFocus={() => setClienteMenuOpen(true)}
                    onBlur={() => window.setTimeout(() => setClienteMenuOpen(false), 180)}
                    isInvalid={!!errors.cliente}
                  />
                  {clienteMenuOpen && clientesFiltrados.length > 0 ? (
                    <ListGroup
                      className="position-absolute w-100 shadow border rounded overflow-auto"
                      style={{ zIndex: 5, top: '100%', marginTop: 2, maxHeight: 220 }}
                    >
                      {clientesFiltrados.map((c) => (
                        <ListGroup.Item
                          key={c.id_cliente}
                          action
                          className="py-2 px-3"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            onSelectCliente(String(c.id_cliente));
                            setClienteMenuOpen(false);
                          }}
                        >
                          {c.nombre}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : null}
                  <Form.Control.Feedback type="invalid">{errors.cliente}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>TAX ID (PI / cliente)</Form.Label>
                  <Form.Control
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                    isInvalid={!!errors.taxId}
                  />
                  <Form.Control.Feedback type="invalid">{errors.taxId}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>PIN CODE</Form.Label>
                  <Form.Control
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value)}
                    isInvalid={!!errors.pinCode}
                  />
                  <Form.Control.Feedback type="invalid">{errors.pinCode}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Dirección cliente (encabezado PDF)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={clienteDireccion}
                    onChange={(e) => setClienteDireccion(e.target.value)}
                    isInvalid={!!errors.clienteDireccion}
                  />
                  <Form.Control.Feedback type="invalid">{errors.clienteDireccion}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>País origen / Country of origin</Form.Label>
                  <Form.Control
                    value={paisOrigen}
                    onChange={(e) => setPaisOrigen(e.target.value)}
                    isInvalid={!!errors.paisOrigen}
                  />
                  <Form.Control.Feedback type="invalid">{errors.paisOrigen}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>País destino / Destination Country</Form.Label>
                  <Form.Control
                    value={paisDestino}
                    onChange={(e) => setPaisDestino(e.target.value)}
                    isInvalid={!!errors.paisDestino}
                  />
                  <Form.Control.Feedback type="invalid">{errors.paisDestino}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Nombre nave / Vessel name</Form.Label>
                  <Form.Control
                    value={nombreNave}
                    onChange={(e) => setNombreNave(e.target.value)}
                    isInvalid={!!errors.nombreNave}
                  />
                  <Form.Control.Feedback type="invalid">{errors.nombreNave}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Puerto origen / Loading Port</Form.Label>
                  <Form.Control
                    value={puertoOrigen}
                    onChange={(e) => setPuertoOrigen(e.target.value)}
                    isInvalid={!!errors.puertoOrigen}
                  />
                  <Form.Control.Feedback type="invalid">{errors.puertoOrigen}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Puerto destino / Unloading Port</Form.Label>
                  <Form.Control
                    value={puertoDestino}
                    onChange={(e) => setPuertoDestino(e.target.value)}
                    isInvalid={!!errors.puertoDestino}
                  />
                  <Form.Control.Feedback type="invalid">{errors.puertoDestino}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {(empresaEmisor === EMPRESA_PTC || empresaEmisor === EMPRESA_PTB) && (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Lugar de entrega / Place of delivery</Form.Label>
                    <Form.Control
                      value={lugarEntrega}
                      onChange={(e) => setLugarEntrega(e.target.value)}
                      isInvalid={!!errors.lugarEntrega}
                    />
                    <Form.Control.Feedback type="invalid">{errors.lugarEntrega}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Nombre BL</Form.Label>
                  <Form.Control
                    value={nombreBl}
                    onChange={(e) => setNombreBl(e.target.value)}
                    isInvalid={!!errors.nombreBl}
                  />
                  <Form.Control.Feedback type="invalid">{errors.nombreBl}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="position-relative">
                  <Form.Label>N° de contrato / Contract No.</Form.Label>
                  {idsProformaSeleccion.length > 0 ? (
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {idsProformaSeleccion.map((idStr) => {
                        const pi = proformasFiltradas.find(
                          (p) => String(p.id_proforma) === String(idStr)
                        );
                        const lbl = pi != null ? proformaEtiqueta(pi) : `id ${idStr}`;
                        return (
                          <Badge
                            key={String(idStr)}
                            bg="secondary"
                            className="d-inline-flex align-items-center gap-1 fw-normal py-1 px-2"
                          >
                            <span>{lbl}</span>
                          </Badge>
                        );
                      })}
                    </div>
                  ) : null}
                  <Form.Control
                    readOnly
                    value={contractNo}
                    placeholder="Contratos definidos en el Packing List"
                  />
                  <Form.Text className="text-muted">
                    Los contratos provienen del Packing List. Para modificarlos, edite primero ese Packing List.
                  </Form.Text>

                </Form.Group>
              </Col>
            </Row>

            {empresaEmisor === EMPRESA_PTB ? (
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>N° Contrato / Contract No.</Form.Label>
                    <Form.Control
                      value={contractNoPtb}
                      onChange={(e) => setContractNoPtb(e.target.value)}
                      placeholder="Opcional"
                    />
                  </Form.Group>
                </Col>
              </Row>
            ) : null}

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>N° pedido compra / Purchase Order</Form.Label>
                  <Form.Control
                    value={purchaseOrder}
                    onChange={(e) => {
                      purchaseOrderEditadoRef.current = true;
                      setPurchaseOrder(e.target.value);
                    }}
                    isInvalid={!!errors.purchaseOrder}
                  />
                  <Form.Control.Feedback type="invalid">{errors.purchaseOrder}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Net Weight (Kg)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={netWeight}
                    onChange={(e) => setNetWeight(e.target.value)}
                    isInvalid={!!errors.netWeight}
                  />
                  <Form.Control.Feedback type="invalid">{errors.netWeight}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Gross Weight (Kg)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={grossWeight}
                    onChange={(e) => setGrossWeight(e.target.value)}
                    isInvalid={!!errors.grossWeight}
                  />
                  <Form.Control.Feedback type="invalid">{errors.grossWeight}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Sales terms</Form.Label>
                  <Form.Control
                    value={salesTerms}
                    onChange={(e) => setSalesTerms(e.target.value)}
                    isInvalid={!!errors.salesTerms}
                  />
                  <Form.Control.Feedback type="invalid">{errors.salesTerms}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Incoterms (Sales condition PI)</Form.Label>
                  <Form.Control
                    value={incotermsTexto}
                    onChange={(e) => setIncotermsTexto(e.target.value)}
                    isInvalid={!!errors.incotermsTexto}
                  />
                  <Form.Control.Feedback type="invalid">{errors.incotermsTexto}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Payment terms (PI)</Form.Label>
                  <Form.Control
                    value={paymentTermsPtc}
                    onChange={(e) => setPaymentTermsPtc(e.target.value)}
                    isInvalid={!!errors.paymentTermsPtc}
                  />
                  <Form.Control.Feedback type="invalid">{errors.paymentTermsPtc}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Quantity (total volumen PL)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    min="0"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="Total M³ del packing list"
                    isInvalid={!!errors.quantity}
                  />
                  <Form.Control.Feedback type="invalid">{errors.quantity}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Unidad Quantity / resumen</Form.Label>
                  <Form.Select
                    value={unidadVolumenPtc}
                    onChange={(e) => setUnidadVolumenPtc(e.target.value)}
                  >
                    <option value="M3">M3</option>
                    <option value="CBM">CBM</option>
                  </Form.Select>
                  <Form.Text className="text-muted">Por defecto M3</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {mostrarBloquePtb ? (
              <>
                <hr />
                <h6 className="mb-3 text-muted">Datos Invoice PTB (segunda impresión — logo PTB)</h6>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>N° Contrato / Contract No.</Form.Label>
                      <Form.Control
                        value={contractNoPtb}
                        onChange={(e) => setContractNoPtb(e.target.value)}
                        placeholder="Opcional"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Purchase Order (PTB)</Form.Label>
                      <Form.Control
                        value={purchaseOrderPtb}
                        onChange={(e) => {
                          purchaseOrderPtbEditadoRef.current = true;
                          setPurchaseOrderPtb(e.target.value);
                        }}
                        isInvalid={!!errors.purchaseOrderPtb}
                      />
                      <Form.Control.Feedback type="invalid">{errors.purchaseOrderPtb}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Terms of Sale (PTB)</Form.Label>
                      <Form.Control
                        value={termsOfSalePtb}
                        onChange={(e) => setTermsOfSalePtb(e.target.value)}
                        isInvalid={!!errors.termsOfSalePtb}
                      />
                      <Form.Control.Feedback type="invalid">{errors.termsOfSalePtb}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Clause (PTB)</Form.Label>
                      <Form.Control
                        value={clausePtb}
                        onChange={(e) => setClausePtb(e.target.value)}
                        isInvalid={!!errors.clausePtb}
                      />
                      <Form.Control.Feedback type="invalid">{errors.clausePtb}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Payment terms (PTB)</Form.Label>
                      <Form.Control
                        value={paymentTermsPtb}
                        onChange={(e) => setPaymentTermsPtb(e.target.value)}
                        isInvalid={!!errors.paymentTermsPtb}
                      />
                      <Form.Control.Feedback type="invalid">{errors.paymentTermsPtb}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                {lineasMercanciaPtbEditables.length > 0 ? (
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Label>Precios mercancía Invoice PTB (desde Excel packing list)</Form.Label>
                      {errors.mercanciaPtb ? (
                        <Alert variant="danger" className="py-2 mb-2">{errors.mercanciaPtb}</Alert>
                      ) : null}
                      <Table bordered hover responsive size="sm" className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Producto</th>
                            <th>Grado</th>
                            <th>Pcs/paq</th>
                            <th>Medida</th>
                            <th>M³</th>
                            <th>Precio unitario USD/M³</th>
                            <th>Total USD línea</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineasMercanciaPtbEditables.map((linea, index) => (
                            <tr key={`ptb-block-${productMeasureGradeKey(linea)}-${index}`}>
                              <td>{linea.productName || '—'}</td>
                              <td>{linea.grado || '—'}</td>
                              <td>{formatPcsPaqInvoiceCell(linea)}</td>
                              <td>
                                {[linea.espessura, linea.largura, linea.comprimento]
                                  .filter(Boolean)
                                  .join(' × ') || '—'}
                              </td>
                              <td>{linea.quantityM3 ?? '—'}</td>
                              <td style={{ minWidth: 190 }}>
                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={linea.unitPriceUsd ?? ''}
                                  onChange={(e) => cambiarPrecioUnitarioPtb(index, e.target.value)}
                                />
                              </td>
                              <td>{linea.totalValueUsd ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                        {ptbMercanciaTableValorTotalFooter}
                      </Table>
                      <Form.Text className="text-muted">
                        Precio propuesto desde el Excel; el PDF PTB usa el valor confirmado aquí.
                      </Form.Text>
                    </Col>
                  </Row>
                ) : (
                  ptbValorTotalUsdSinTabla
                )}
              </>
            ) : null}
          </>
        )}
      </form>
    </ElegantModal>
    <InvoicePtcPreviewModal
      show={show && showPreview}
      pdfUrl={previewUrlActual}
      loading={previewLoading}
      error={previewError}
      warning={previewWarning}
      onClose={cerrarPreview}
      onDownload={handleDescargarPreview}
      title={previewTitle}
      iframeTitle={previewIframeTitle}
      showVariantSwitch={empresaEmisor === EMPRESA_PTC}
      selectedVariant={previewVariant}
      onSelectVariant={setPreviewVariant}
    />
    </>
  );
};

export default AgregarInvoice;
